import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Layout, ConfigProvider, App as AntdApp } from 'antd';
import { Provider } from 'react-redux';
import { Toaster } from 'react-hot-toast';
import store from './store';
import Home from './pages/Home';
import Login from './pages/Login';
import DetailHistory from './pages/DetailHistory';
import AffiliateDetail from './pages/AffiliateDetail';
import StudyProgramDetail from './pages/StudyProgramDetail';
import RankingSystem from './pages/RankingSystem';
import AdminHashtags from './pages/AdminHashtags';
import AdminScheduler from './pages/AdminScheduler';
import AdminUsers from './pages/AdminUsers';
import AdminConfig from './pages/AdminConfig';
import VerificatorPanel from './pages/VerificatorPanel';
import MyVideos from './pages/MyVideos';
import CertificationInfo from './pages/CertificationInfo';
import CertificationUpload from './pages/CertificationUpload';
import MyCertifications from './pages/MyCertifications';
import CertificationVerification from './pages/CertificationVerification';
import UnifiedVerification from './pages/UnifiedVerification';
import OAuthCallback from './components/OAuthCallback';
import ProtectedRoute from './components/ProtectedRoute';
import './App.css';

const { Content, Footer } = Layout;

function App() {
  return (
    <Provider store={store}>
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: '#14B8A6',
            colorSuccess: '#14B8A6',
            colorWarning: 'rgb(255, 249, 175)',
            colorError: '#ff4d4f',
            colorInfo: '#14B8A6',
            borderRadius: 8,
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          },
        }}
      >
        <AntdApp>
          <Layout className="app-layout">
        <Routes>
          {/* Routes with Header and Footer */}
          <Route path="/" element={
            <>
              <OAuthCallback />
              <Content className="app-content">
                <Home />
              </Content>
              <Footer className="app-footer">
                <div className="footer-content">
                  <div className="footer-section">
                    <h3>GAMIFIKASI #AI-ITERA</h3>
                    <p>Sistem perankingan penggunaan AI untuk mahasiswa dan dosen di kampus ITERA</p>
                  </div>
                  <div className="footer-section">
                    <h4>Quick Links</h4>
                    <ul className="footer-links">
                      <li><a href="/">Leaderboard</a></li>
                      <li><a href="/ranking-system">Skema Perankingan</a></li>
                      <li><a href="/login">Login</a></li>
                      <li><a href="/admin">Admin Panel</a></li>
                    </ul>
                  </div>
                  <div className="footer-section">
                    <h4>Contact</h4>
                    <p>Pusat Keamanan Digital dan Teknologi Cerdas</p>
                    <p>Lembaga Penelitian dan Pengabdian kepada Masyarakat</p>
                    <p>Institut Teknologi Sumatera</p>
                  </div>
                </div>
                <div className="footer-bottom">
                  <p>© {new Date().getFullYear()} GAMIFIKASI #AI-ITERA. All rights reserved.</p>
                  <p>Developed by Pusat Keamanan Digital dan Teknologi Cerdas ITERA</p>
                  <p>Lembaga Penelitian dan Pengabdian kepada Masyarakat</p>
                  <p>Institut Teknologi Sumatera</p>
                </div>
              </Footer>
            </>
          } />

          {/* Detail History Route (with Header, no Footer for cleaner view) */}
          <Route path="/history/:userId" element={
            <>
              <Content className="app-content">
                <DetailHistory />
              </Content>
            </>
          } />

          {/* Affiliate Detail Route */}
          <Route path="/affiliate/:affiliateId" element={
            <>
              <Content className="app-content">
                <AffiliateDetail />
              </Content>
            </>
          } />

          {/* Study Program Detail Route */}
          <Route path="/study-program/:unitCode" element={
            <>
              <Content className="app-content">
                <StudyProgramDetail />
              </Content>
            </>
          } />

          {/* Ranking System Route - Public Access */}
          <Route path="/ranking-system" element={
            <>
              <Content className="app-content full-page">
                <RankingSystem />
              </Content>
            </>
          } />

          {/* My Videos Route - Protected for authenticated users (uses UserLayout) */}
          <Route path="/my-videos" element={
            <ProtectedRoute allowedRoles={['user', 'verifier', 'admin']}>
              <OAuthCallback />
              <MyVideos />
            </ProtectedRoute>
          } />

          {/* Certification Routes - Protected for authenticated users */}
          <Route path="/certifications/info" element={
            <>
              <Content className="app-content">
                <CertificationInfo />
              </Content>
            </>
          } />
          <Route path="/certifications/upload" element={
            <ProtectedRoute allowedRoles={['user', 'verifier', 'admin']}>
              <OAuthCallback />
              <CertificationUpload />
            </ProtectedRoute>
          } />
          <Route path="/certifications" element={
            <ProtectedRoute allowedRoles={['user', 'verifier', 'admin']}>
              <OAuthCallback />
              <MyCertifications />
            </ProtectedRoute>
          } />
          <Route path="/certifications/verify" element={
            <ProtectedRoute allowedRoles={['verifier', 'admin']}>
              <OAuthCallback />
              <CertificationVerification />
            </ProtectedRoute>
          } />

          {/* Login Page (no Header/Footer for focused experience) */}
          <Route path="/login" element={
            <Content className="app-content full-page">
              <Login />
            </Content>
          } />

          {/* Admin Panel Routes - Protected for admins only */}
          <Route path="/admin" element={
            <ProtectedRoute allowedRoles={['admin', 'verifier']}>
              <OAuthCallback />
              <UnifiedVerification />
            </ProtectedRoute>
          } />
          <Route path="/verification" element={
            <ProtectedRoute allowedRoles={['admin', 'verifier']}>
              <OAuthCallback />
              <UnifiedVerification />
            </ProtectedRoute>
          } />
          <Route path="/admin/hashtags" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <OAuthCallback />
              <AdminHashtags />
            </ProtectedRoute>
          } />
          <Route path="/admin/scheduler" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <OAuthCallback />
              <AdminScheduler />
            </ProtectedRoute>
          } />
          <Route path="/admin/users" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <OAuthCallback />
              <AdminUsers />
            </ProtectedRoute>
          } />
          <Route path="/admin/config" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <OAuthCallback />
              <AdminConfig />
            </ProtectedRoute>
          } />

          {/* Verificator Panel - Protected for verifiers and admins */}
          <Route path="/verificator" element={
            <ProtectedRoute allowedRoles={['verifier', 'admin']}>
              <OAuthCallback />
              <VerificatorPanel />
            </ProtectedRoute>
          } />

          {/* Redirect /home to / */}
          <Route path="/home" element={<Navigate to="/" replace />} />

          {/* 404 Not Found */}
          <Route path="*" element={
            <>
              <Content className="app-content">
                <div className="not-found-page">
                  <div className="not-found-content">
                    <h1>404</h1>
                    <h2>Halaman Tidak Ditemukan</h2>
                    <p>Maaf, halaman yang Anda cari tidak ada.</p>
                    <a href="/" className="back-home-btn">
                      Kembali ke Beranda
                    </a>
                  </div>
                </div>
              </Content>
            </>
          } />
        </Routes>
          </Layout>
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
              },
              success: {
                duration: 3000,
                iconTheme: {
                  primary: '#14B8A6',
                  secondary: '#fff',
                },
              },
              error: {
                duration: 5000,
                iconTheme: {
                  primary: '#ff4d4f',
                  secondary: '#fff',
                },
              },
            }}
          />
        </AntdApp>
      </ConfigProvider>
    </Provider>
  );
}

export default App;
